/**
 * Script upload step — REAL PDF ingest (Agent 01).
 *
 * Flow when the user drops / chooses a PDF:
 *   1. Validate the file (must be a PDF, must be under the size cap).
 *   2. Extract the raw text with pdf.js.
 *   3. POST the raw text to the Worker `/chat` route. Claude normalises
 *      the screenplay to a clean Final-Draft shape and returns STRUCTURED
 *      JSON: scenes (heading + body), unique characters, locations, and
 *      notable assets/props.
 *   4. Write the result atomically into StudioStore
 *      (`ingestParsedScript`), auto-creating the cast / locations /
 *      assets the parser found (living-document, _GLOBAL.md G6).
 *
 * The pipeline itself lives in `scriptIngest.ts` so the very same pipe
 * powers the in-page "drop a PDF" affordance on the empty Script view.
 * This panel is the dedicated upload destination + its status/summary UI.
 *
 * Every failure mode surfaces inline (non-PDF, oversized file, empty /
 * image-only PDF, network or model failure, empty extraction) with a
 * retry affordance — we never silently drop the user's upload.
 */

import { BasePanel } from "./BasePanel";
import { buildHelperParagraph, buildPixelBox, buildStudioButton } from "./dom";
import type { PanelManager } from "../PanelManager";
import type { StudioStore } from "../StudioStore";
import {
  analyzeScript,
  clampText,
  countParsedScenes,
  extractPdfText,
  formatBytes,
  ingestParsedIntoStore,
  looksLikePdf,
  MAX_FILE_BYTES,
  resolveWorkerBaseUrl,
  toUserMessage,
  type IngestCounts,
} from "./scriptIngest";
import "../styles/script-upload.css";

interface ScriptUploadPanelOptions {
  store: StudioStore;
  panelManager: PanelManager;
}

const SEED_SCENE_BODY =
  '[Scene starts here. Replace this with your opening action lines or dialogue, or just tell Koko: "add a scene where ..."]';

type UploadPhase = "idle" | "extracting" | "analyzing" | "error" | "success";

export class ScriptUploadPanel extends BasePanel {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private dropZoneEl: HTMLDivElement | null = null;
  private fileInputEl: HTMLInputElement | null = null;
  private statusEl: HTMLDivElement | null = null;
  private summaryEl: HTMLDivElement | null = null;
  /** Guards against concurrent uploads (double drop / click). */
  private isWorking = false;

  constructor(options: ScriptUploadPanelOptions) {
    super();
    this.store = options.store;
    this.panelManager = options.panelManager;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");

    const box = buildPixelBox();
    box.fill.appendChild(
      buildHelperParagraph(
        "Drop in a screenplay PDF and Koko will read it, clean it into Final Draft format, then pull out your scenes, cast, locations, and key props. Skip it to draft scenes by hand instead.",
      ),
    );

    const dropZone = document.createElement("div");
    dropZone.className = "studio-drop-zone";
    dropZone.dataset.kokoAction = "script-drop-zone";

    const icon = document.createElement("div");
    icon.className = "studio-drop-zone-icon";
    icon.textContent = "▣";

    const label = document.createElement("div");
    label.textContent = "Upload PDF Script";

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "application/pdf,.pdf";
    fileInput.className = "studio-hidden-file-input";
    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      if (file) void this.handleUpload(file);
    });

    const chooseButton = buildStudioButton("Choose PDF file", {
      variant: "primary",
      onClick: () => {
        if (!this.isWorking) fileInput.click();
      },
    });
    chooseButton.dataset.kokoAction = "script-choose";

    dropZone.appendChild(icon);
    dropZone.appendChild(label);
    dropZone.appendChild(chooseButton);
    dropZone.appendChild(fileInput);

    dropZone.addEventListener("dragover", (event) => {
      event.preventDefault();
      if (this.isWorking) return;
      dropZone.classList.add("is-dragover");
    });
    dropZone.addEventListener("dragleave", () => {
      dropZone.classList.remove("is-dragover");
    });
    dropZone.addEventListener("drop", (event) => {
      event.preventDefault();
      dropZone.classList.remove("is-dragover");
      if (this.isWorking) return;
      const file = event.dataTransfer?.files?.[0];
      if (file) void this.handleUpload(file);
    });

    box.fill.appendChild(dropZone);

    const status = document.createElement("div");
    status.className = "studio-upload-status";
    status.hidden = true;
    box.fill.appendChild(status);

    const summary = document.createElement("div");
    summary.className = "studio-upload-summary";
    summary.hidden = true;
    box.fill.appendChild(summary);

    const skipRow = document.createElement("div");
    skipRow.className = "studio-button-row";
    skipRow.style.marginTop = "16px";
    skipRow.appendChild(
      buildStudioButton("Skip — write scenes by hand", {
        onClick: () => {
          if (this.store.get("scenes").length === 0) {
            this.store.addScene(SEED_SCENE_BODY);
          }
          this.panelManager.open("script_editor");
        },
      }),
    );
    box.fill.appendChild(skipRow);

    root.appendChild(box.outer);
    this.dropZoneEl = dropZone;
    this.fileInputEl = fileInput;
    this.statusEl = status;
    this.summaryEl = summary;
    return root;
  }

  protected onShown(): void {
    if (this.fileInputEl) this.fileInputEl.value = "";
    if (this.dropZoneEl) this.dropZoneEl.classList.remove("is-dragover");
  }

  // ------------------------------------------------------------------
  // Upload pipeline (delegates to scriptIngest.ts)
  // ------------------------------------------------------------------

  private async handleUpload(file: File): Promise<void> {
    if (this.isWorking) return;

    if (!looksLikePdf(file)) {
      this.setStatus(
        "error",
        "That doesn't look like a PDF.",
        `“${file.name}” isn't a PDF file. Export your screenplay as PDF and try again.`,
      );
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.setStatus(
        "error",
        "That PDF is too large.",
        `“${file.name}” is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_FILE_BYTES)}.`,
      );
      return;
    }
    if (file.size === 0) {
      this.setStatus("error", "That PDF is empty.", `“${file.name}” has no content to read.`);
      return;
    }

    this.beginWork();
    try {
      this.setStatus("extracting", `Reading “${file.name}”…`);
      const rawText = await extractPdfText(file);

      if (rawText.trim().length === 0) {
        this.setStatus(
          "error",
          "No text found in that PDF.",
          "This looks like a scanned or image-only PDF. Upload a text-based screenplay export instead.",
        );
        return;
      }

      const { text, truncated } = clampText(rawText);
      this.setStatus(
        "analyzing",
        "Koko is reading the script and pulling out scenes, cast, and locations…",
      );
      const parsed = await analyzeScript(text, resolveWorkerBaseUrl());

      if (countParsedScenes(parsed) === 0) {
        this.setStatus(
          "error",
          "Couldn't find any scenes.",
          "Koko read the file but couldn't break it into scenes. Check that the PDF is a screenplay, or skip and draft by hand.",
        );
        return;
      }

      const written = ingestParsedIntoStore(this.store, file.name, parsed);
      this.setStatus(
        "success",
        `Imported “${file.name}”.`,
        truncated
          ? "Heads up: the script was long, so the tail was trimmed before analysis."
          : undefined,
      );
      this.renderSummary(written);
    } catch (error) {
      console.error("[studio] script upload failed:", error);
      this.setStatus("error", "Couldn't import that script.", toUserMessage(error));
    } finally {
      this.endWork();
    }
  }

  // ------------------------------------------------------------------
  // Status / summary rendering
  // ------------------------------------------------------------------

  private beginWork(): void {
    this.isWorking = true;
    this.dropZoneEl?.classList.add("is-busy");
    if (this.summaryEl) this.summaryEl.hidden = true;
  }

  private endWork(): void {
    this.isWorking = false;
    this.dropZoneEl?.classList.remove("is-busy");
  }

  private setStatus(
    phase: UploadPhase,
    message: string,
    detail?: string,
  ): void {
    const status = this.statusEl;
    if (!status) return;
    status.hidden = false;
    status.className = "studio-upload-status";
    if (phase === "error") status.classList.add("is-error");
    if (phase === "success") status.classList.add("is-success");
    status.replaceChildren();

    const inProgress = phase === "extracting" || phase === "analyzing";
    if (inProgress) {
      const spinner = document.createElement("span");
      spinner.className = "studio-upload-spinner";
      status.appendChild(spinner);
    }

    const textWrap = document.createElement("div");
    textWrap.className = "studio-upload-status-text";
    const main = document.createElement("span");
    main.textContent = message;
    textWrap.appendChild(main);
    if (detail) {
      const detailEl = document.createElement("span");
      detailEl.className = "studio-upload-status-detail";
      detailEl.textContent = detail;
      textWrap.appendChild(detailEl);
    }
    status.appendChild(textWrap);

    if (phase === "error") {
      const retry = buildStudioButton("Try another file", {
        onClick: () => {
          if (this.isWorking) return;
          status.hidden = true;
          this.fileInputEl?.click();
        },
      });
      retry.classList.add("studio-upload-retry");
      retry.dataset.kokoAction = "script-retry";
      status.appendChild(retry);
    }

    if (phase === "success") {
      const open = buildStudioButton("Open script editor", {
        variant: "primary",
        onClick: () => this.panelManager.open("script_editor"),
      });
      open.classList.add("studio-upload-retry");
      open.dataset.kokoAction = "script-open-editor";
      status.appendChild(open);
    }
  }

  private renderSummary(written: IngestCounts): void {
    const summary = this.summaryEl;
    if (!summary) return;
    summary.hidden = false;
    summary.replaceChildren();

    const title = document.createElement("p");
    title.className = "studio-upload-summary-title";
    title.textContent = "What Koko pulled out";
    summary.appendChild(title);

    const grid = document.createElement("div");
    grid.className = "studio-upload-summary-grid";
    const stats: Array<[number, string]> = [
      [written.scenes, written.scenes === 1 ? "scene" : "scenes"],
      [written.characters, written.characters === 1 ? "character" : "characters"],
      [written.locations, written.locations === 1 ? "location" : "locations"],
      [written.assets, written.assets === 1 ? "asset" : "assets"],
    ];
    for (const [count, label] of stats) {
      const stat = document.createElement("div");
      stat.className = "studio-upload-stat";
      const number = document.createElement("span");
      number.className = "studio-upload-stat-number";
      number.textContent = String(count);
      const labelEl = document.createElement("span");
      labelEl.className = "studio-upload-stat-label";
      labelEl.textContent = label;
      stat.appendChild(number);
      stat.appendChild(labelEl);
      grid.appendChild(stat);
    }
    summary.appendChild(grid);
  }
}
